import React, {
  createContext,
  FC,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { GetMe_me_permissions } from '../graphql/user/types/GetMe';
import { Operation, Resource } from '../common/constants';
import { canUser } from '../utils/user';

export interface IUser {
  id: number | null;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  recordDirectoryToken: string;
  roles: string[];
  permissions: (GetMe_me_permissions | null)[];
}

const initialUser: IUser = {
  id: null,
  email: '',
  firstName: '',
  lastName: '',
  avatarUrl: '',
  recordDirectoryToken: '',
  roles: [],
  permissions: [],
};

export interface IUserContext {
  me: IUser;
  setMe: (me: IUser) => void;
  canI: (
    operation: Operation | null | undefined,
    resource: Resource | null | undefined,
  ) => boolean;
}

export const UserContext = createContext<IUserContext>({
  me: initialUser,
  setMe: () => {},
  canI: () => false,
});

const UserProvider: FC = ({ children }) => {
  const [user, setUser] = useState<IUser>(initialUser);

  const canI = useCallback<{
    (
      operation: Operation | null | undefined,
      resource: Resource | null | undefined,
    ): boolean;
  }>(
    (operation, resource) =>
      !!user && canUser(operation, resource, user?.permissions),
    [user],
  );

  const value = useMemo<IUserContext>(
    () => ({
      me: user,
      setMe: (me: IUser) => {
        setUser((prev) => ({ ...prev, ...me }));
      },
      canI,
    }),
    [user, canI],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserProvider;
